import { Location, DOCUMENT } from "@angular/common";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import {
    MSAL_GUARD_CONFIG,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService,
} from "@azure/msal-angular";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { environment } from "../../environments/environment";

type GuardResult = boolean | UrlTree;

export const SESSION_EXPIRED_FLAG = "calsium-session-expired";

function loginUrlTree(router: Router, returnUrl?: string): UrlTree {
    const queryParams = returnUrl && returnUrl !== "/" ? { returnUrl } : undefined;
    return router.createUrlTree(["/login"], { queryParams });
}

function markSessionExpired(): void {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
}

/**
 * Wraps MsalInterceptor (which still attaches tokens and renews them silently) but, when a silent
 * renewal fails during an API call, shows the CALSIUM sign-in page instead of redirecting to Microsoft.
 */
@Injectable()
export class AppMsalInterceptor implements HttpInterceptor {
    private readonly inner: MsalInterceptor;

    constructor(
        @Inject(MSAL_INTERCEPTOR_CONFIG) config: MsalInterceptorConfiguration,
        msal: MsalService,
        location: Location,
        broadcast: MsalBroadcastService,
        @Inject(DOCUMENT) document: Document,
        private router: Router
    ) {
        this.inner = new MsalInterceptor(config, msal, location, broadcast, document);
        // MsalInterceptor calls this when silent renewal fails; its default starts the Microsoft redirect.
        (this.inner as any).acquireTokenInteractively = () => {
            if (!this.router.url.startsWith("/login")) {
                markSessionExpired();
                this.router.navigateByUrl(loginUrlTree(this.router, this.router.url));
            }
            return EMPTY;
        };
    }

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.inner.intercept(req, next);
    }
}

/**
 * Wraps MsalGuard (which still handles the redirect back from Microsoft) but shows the
 * CALSIUM sign-in page instead of jumping straight to Microsoft when nobody is signed in.
 */
@Injectable()
export class AppMsalGuard {
    private readonly inner: MsalGuard;

    constructor(
        @Inject(MSAL_GUARD_CONFIG) config: MsalGuardConfiguration,
        broadcast: MsalBroadcastService,
        private msal: MsalService,
        location: Location,
        private router: Router
    ) {
        this.inner = new MsalGuard(config, broadcast, msal, location, router);
        // MsalGuard calls this when there is no account; its default starts the Microsoft redirect.
        (this.inner as any).loginInteractively = (state: RouterStateSnapshot) =>
            of(loginUrlTree(this.router, state?.url));
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<GuardResult> {
        return this.verifySession(this.inner.canActivate(route, state), state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<GuardResult> {
        return this.verifySession(this.inner.canActivateChild(route, state), state);
    }

    canMatch(): Observable<GuardResult> {
        return this.inner.canMatch();
    }

    // An account can stay cached after its tokens expired; if they can't be renewed silently, sign in again.
    private verifySession(result: Observable<GuardResult>, state: RouterStateSnapshot): Observable<GuardResult> {
        return result.pipe(
            switchMap((allowed) => {
                if (allowed !== true) {
                    return of(allowed);
                }
                const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];
                return this.msal
                    .acquireTokenSilent({ scopes: [...environment.protectedResources.api.scopes], account })
                    .pipe(
                        map((): GuardResult => true),
                        catchError(() => {
                            markSessionExpired();
                            return of<GuardResult>(loginUrlTree(this.router, state.url));
                        })
                    );
            })
        );
    }
}
